/**
 * Admin API v1 contracts (/api/admin/v1), shared by the API (validation) and the admin panel (forms,
 * typed client). Field names follow the stored documents (snake_case), the same as the public API.
 */
import { z } from 'zod';

export const objectId = z.string().regex(/^[0-9a-fA-F]{24}$/, 'Invalid id');
/**
 * [lon, lat]. Some stored routes carry a third number (altitude, e.g. from KML/GPS imports): it is
 * accepted and dropped, so saving such a shape also cleans it.
 */
const position = z
  .array(z.number())
  .min(2, 'A point needs a longitude and a latitude')
  .max(3, 'A point has at most longitude, latitude and altitude')
  .transform((c) => [c[0]!, c[1]!])
  .pipe(z.tuple([z.number().min(-180).max(180), z.number().min(-90).max(90)]));
const ring = z.array(position).min(4);

export const pointGeometry = z.object({ type: z.literal('Point'), coordinates: position });
export const lineStringGeometry = z.object({
  type: z.literal('LineString'),
  coordinates: z.array(position).min(2),
});
export const polygonGeometry = z.object({ type: z.literal('Polygon'), coordinates: z.array(ring).min(1) });
export type PointGeometry = z.infer<typeof pointGeometry>;
export type LineStringGeometry = z.infer<typeof lineStringGeometry>;
export type PolygonGeometry = z.infer<typeof polygonGeometry>;

const color = z.string().regex(/^#[0-9a-fA-F]{3,8}$/, 'Use a hex colour like #e53935');
const url = z.string().url();
/** Optional link: empty or a URL (legacy records often have empty links). */
const optionalUrl = z.union([z.literal(''), url]).default('');
const optionalText = z.string().trim().max(5000).optional().default('');

// ---------------------------------------------------------------- lists

export const listQuery = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(500).default(25),
  /** Field name, prefixed with "-" for descending. */
  sort: z
    .string()
    .regex(/^-?[a-z_]+$/)
    .optional(),
  q: z.string().trim().max(100).optional(),
});
export type ListQuery = z.infer<typeof listQuery>;

export interface Page<T> {
  items: T[];
  page: number;
  pageSize: number;
  total: number;
}

export interface ApiErrorBody {
  error: { code: string; message: string; fields?: Record<string, string> };
}

// ---------------------------------------------------------------- auth & users

export const roles = ['admin', 'operator'] as const;
export const role = z.enum(roles);
export type Role = z.infer<typeof role>;

export const loginInput = z.object({
  email: z.string().trim().toLowerCase().email(),
  password: z.string().min(1),
  remember: z.boolean().optional().default(false),
});
export type LoginInput = z.input<typeof loginInput>;

export interface SessionUser {
  id: string;
  email: string;
  role: Role;
}
export interface AuthResult {
  accessToken: string;
  expiresIn: number;
  user: SessionUser;
}

export const userCreate = z.object({
  email: z.string().trim().toLowerCase().email(),
  password: z.string().min(10, 'At least 10 characters'),
  role,
  is_active: z.boolean().default(true),
});
export const userUpdate = z.object({
  role: role.optional(),
  is_active: z.boolean().optional(),
  password: z.string().min(10, 'At least 10 characters').optional(),
});
export const changePasswordInput = z.object({
  current_password: z.string().min(1),
  new_password: z.string().min(10, 'At least 10 characters'),
});

// ---------------------------------------------------------------- network

export const stationInput = z.object({
  name: z.string().trim().min(1).max(120),
  description: optionalText,
  geometry: pointGeometry,
  /** Lines this station belongs to. New memberships are appended to the end of the line. */
  lines: z.array(objectId).default([]),
});

export const lineInput = z.object({
  name: z.string().trim().min(1).max(120),
  description: optionalText,
  geometry: lineStringGeometry,
  /** Stations in travel order. The first and last are the terminuses. */
  ordered_stations: z.array(objectId).min(2, 'A direction needs at least two stations'),
});

export const zoneInput = z.object({
  name: z.string().trim().min(1).max(120),
  description: optionalText,
  bus_sign: z.string().trim().min(1).max(10),
  color,
  sort: z.number().int().default(0),
  is_active: z.boolean().default(false),
  geometry_going: polygonGeometry,
  geometry_return: polygonGeometry,
  on_line_going: objectId.nullable().optional(),
  on_line_return: objectId.nullable().optional(),
});

export const busUpdate = z.object({
  name: z.string().trim().max(120).optional(),
  description: z.string().trim().max(1000).optional(),
  /** A bus, or another vehicle (kept out of the apps and statistics); 'new' only comes from Arvento. */
  vehicle_type: z.enum(['bus', 'other']).optional(),
  vehicle_note: z.string().trim().max(120).optional(),
});
export const busZoneInput = z.object({ zone: objectId.nullable() });

const time = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use HH:MM');
export const scheduleInput = z.object({
  line: objectId,
  station: objectId,
  week: z.array(time).default([]),
  weekend: z.array(time).default([]),
});

export const outOfServiceAreaInput = z.object({
  name: z.string().trim().min(1).max(120),
  description: optionalText,
  geometry: polygonGeometry,
});

export const interestPointInput = z.object({
  title: z.string().trim().min(1).max(200),
  category: z.string().trim().max(100).default(''),
  latitude: z.number().min(-90).max(90),
  longitude: z.number().min(-180).max(180),
  icon: z.string().default(''),
  photo: z.string().default(''),
  is_active: z.boolean().default(false),
  link: z.string().default(''),
  description: z.string().trim().min(1),
});

// ---------------------------------------------------------------- content

export const newsInput = z.object({
  title: z.string().trim().min(1).max(200),
  short_description: z.string().trim().min(1).max(160),
  /** HTML from the rich-text editor. */
  description: z.string().min(1),
  photo: z.string().optional(),
});

export const reportReplyInput = z.object({ description: z.string().trim().min(1).max(5000) });

export const notificationInput = z.object({
  title: z.string().trim().min(1).max(100),
  body: z.string().trim().min(1).max(500),
});

// ---------------------------------------------------------------- advertising

export const campaignInput = z.object({
  name: z.string().trim().min(1).max(120),
  description: optionalText,
  contact: z.string().trim().min(1).max(200),
  emails: z.array(z.string().trim().toLowerCase().email()).default([]),
  notify: z.boolean().default(false),
  is_active: z.boolean().default(false),
});

export const advertisementInput = z.object({
  title: z.string().trim().min(1).max(120),
  description: optionalText,
  photo: z.string().default(''),
  icon: z.string().default(''),
  text: z.string().max(200).default(''),
  text_color: color.default('#FFFFFF'),
  link: optionalUrl,
  ios_link: optionalUrl,
  android_link: optionalUrl,
  is_active: z.boolean().default(false),
  priority: z.number().int().min(1).max(20).default(1),
  campaign_id: objectId,
});

export const pinAdvertisementInput = z.object({
  name: z.string().trim().min(1).max(120),
  description: optionalText,
  geometry: pointGeometry,
  web_view: url,
  icon: z.string().optional(),
  pin_icon: z.string().optional(),
  background_image: z.string().optional(),
  priority: z.number().int().min(1).max(20).default(1),
  is_active: z.boolean().default(false),
  campaign_id: objectId,
});

export const statsQuery = z.object({
  type: z.enum(['view', 'click']).default('view'),
  from: z.coerce.date().optional(),
  to: z.coerce.date().optional(),
});

// ---------------------------------------------------------------- uploads

export const uploadKinds = [
  'newsPhotos',
  'pointIcons',
  'pointPhotos',
  'advertisementIcons',
  'advertisementPhotos',
  'pinAdvertisementPinIcons',
  'pinAdvertisementIcons',
  'pinAdvertisementImages',
] as const;
export const uploadKind = z.enum(uploadKinds);
export type UploadKind = z.infer<typeof uploadKind>;
export interface UploadResult {
  url: string;
}
export * from './timetable.js';
